import { FC } from "react";
import { useSearchParams } from "react-router-dom";
import useI18n from "../../../../hooks/useTranslations";
import Tabs, { Tab } from "../../../ui/Tabs";
import BallastTypes from "./ballastTypes/BallastTypes";
import Ballasts from "./ballasts/Ballasts";
import BasesList from "./bases/Bases";
import Gates from "./gates/Gates";
import GuardrailCompositions from "./guardrailCompositions/GuardrailCompositions";
import MembraneProtections from "./membraneProtections/MembraneProtections";
import Normatives from "./normatives/Normatives";
import RailCaps from "./railCaps/RailCaps";
import RailCorners from "./railCorners/RailCorners";
import RailWallFixings from "./railWallFixings/RailWallFixings";
import Rails from "./rails/Rails";
import Substructures from "./substructures/Substructures";
import BorderSystems from "./systems/BorderSystems";
import ToeBoardExtras from "./toe board extras/ToeBoardExtras";
import ToeBoards from "./toe boards/ToeBoards";
import UprightCaps from "./upright caps/UprightCaps";
import UprightTypes from "./uprightTypes/UprightTypes";
import Uprights from "./uprights/Uprights";
import WaterproofKits from "./waterproofKits/WaterproofKits";

const BorderSettings: FC = () => {
  const i18n = useI18n();
  const [searchParams] = useSearchParams();
  const selectedIndex = Number(searchParams.get("tab")) || 0;

  const tabs: Tab[] = [
    { title: i18n.translation.systems.plural },
    { title: i18n.translation.normatives.plural },
    { title: i18n.translation.substructures.plural },
    { title: i18n.translation.bases.plural, hasSubTabs: true },
    { title: i18n.translation.ballasts.plural, hasSubTabs: true },
    { title: i18n.translation.uprights.plural, hasSubTabs: true },
    { title: i18n.translation.toeBoards.plural, hasSubTabs: true },
    { title: i18n.translation.rails.plural, hasSubTabs: true },
    { title: i18n.translation.gates.plural },
    { title: i18n.translation.membraneProtections.plural },
    { title: i18n.translation.guardrailCompositions.plural },
  ];

  return (
    <Tabs
      urlParameter="tab"
      tabs={tabs}
      contents={[
        <BorderSystems active={selectedIndex === 0} />,
        <Normatives active={selectedIndex === 1} />,
        <Substructures active={selectedIndex === 2} />,
        <BaseSettings active={selectedIndex === 3} />,
        <BallastSettings active={selectedIndex === 4} />,
        <UprightSettings active={selectedIndex === 5} />,
        <ToeBoardSettings active={selectedIndex === 6} />,
        <RailSettings active={selectedIndex === 7} />,
        <Gates active={selectedIndex === 8} />,
        <MembraneProtections active={selectedIndex === 9} />,
        <GuardrailCompositions active={selectedIndex === 10} />,
      ]}
    />
  );
};

export default BorderSettings;

type SubTabsProps = {
  // Passed down from BorderSettings: whether this wrapper's own top-level tab is active.
  active?: boolean;
};

const BaseSettings: FC<SubTabsProps> = ({ active }) => {
  const i18n = useI18n();
  const [searchParams] = useSearchParams();
  const subSelectedIndex = Number(searchParams.get("b_t")) || 0;

  const tabs: Tab[] = [{ title: i18n.translation.items.plural }, { title: i18n.translation.waterproofKits.plural }];

  return (
    <Tabs.SubTab>
      <Tabs
        tabs={tabs}
        urlParameter="b_t"
        contents={[
          <BasesList active={(active ?? true) && subSelectedIndex === 0} />,
          <WaterproofKits active={(active ?? true) && subSelectedIndex === 1} />,
        ]}
      />
    </Tabs.SubTab>
  );
};

const BallastSettings: FC<SubTabsProps> = ({ active }) => {
  const i18n = useI18n();
  const [searchParams] = useSearchParams();
  const subSelectedIndex = Number(searchParams.get("bal_t")) || 0;

  const tabs: Tab[] = [{ title: i18n.translation.items.plural }, { title: i18n.translation.common.types }];

  return (
    <Tabs.SubTab>
      <Tabs
        tabs={tabs}
        urlParameter="bal_t"
        contents={[
          <Ballasts active={(active ?? true) && subSelectedIndex === 0} />,
          <BallastTypes active={(active ?? true) && subSelectedIndex === 1} />,
        ]}
      />
    </Tabs.SubTab>
  );
};

const UprightSettings: FC<SubTabsProps> = ({ active }) => {
  const i18n = useI18n();
  const [searchParams] = useSearchParams();
  const subSelectedIndex = Number(searchParams.get("u_t")) || 0;

  const tabs: Tab[] = [
    { title: i18n.translation.items.plural },
    { title: i18n.translation.common.caps },
    { title: i18n.translation.common.types },
  ];

  return (
    <Tabs.SubTab>
      <Tabs
        tabs={tabs}
        urlParameter="u_t"
        contents={[
          <Uprights active={(active ?? true) && subSelectedIndex === 0} />,
          <UprightCaps active={(active ?? true) && subSelectedIndex === 1} />,
          <UprightTypes active={(active ?? true) && subSelectedIndex === 2} />,
        ]}
      />
    </Tabs.SubTab>
  );
};

const ToeBoardSettings: FC<SubTabsProps> = ({ active }) => {
  const i18n = useI18n();
  const [searchParams] = useSearchParams();
  const subSelectedIndex = Number(searchParams.get("tb_t")) || 0;

  const tabs: Tab[] = [{ title: i18n.translation.items.plural }, { title: i18n.translation.common.extras }];

  return (
    <Tabs.SubTab>
      <Tabs
        tabs={tabs}
        urlParameter="tb_t"
        contents={[
          <ToeBoards active={(active ?? true) && subSelectedIndex === 0} />,
          <ToeBoardExtras active={(active ?? true) && subSelectedIndex === 1} />,
        ]}
      />
    </Tabs.SubTab>
  );
};

const RailSettings: FC<SubTabsProps> = ({ active }) => {
  const i18n = useI18n();
  const [searchParams] = useSearchParams();
  const subSelectedIndex = Number(searchParams.get("r_t")) || 0;

  const tabs: Tab[] = [
    { title: i18n.translation.items.plural },
    { title: i18n.translation.common.caps },
    { title: i18n.translation.common.wallFixings },
    { title: i18n.translation.common.corners },
  ];

  return (
    <Tabs.SubTab>
      <Tabs
        tabs={tabs}
        urlParameter="r_t"
        contents={[
          <Rails active={(active ?? true) && subSelectedIndex === 0} />,
          <RailCaps active={(active ?? true) && subSelectedIndex === 1} />,
          <RailWallFixings active={(active ?? true) && subSelectedIndex === 2} />,
          <RailCorners active={(active ?? true) && subSelectedIndex === 3} />,
        ]}
      />
    </Tabs.SubTab>
  );
};
